import axios from 'axios'
import { Box, Collapse, Stack, Tooltip, useMediaQuery } from '@mui/material'
import { eachDayOfInterval, endOfMonth, format, isWithinInterval, startOfMonth } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { showErrorMessage } from '../../redux/reducers/snackbarReducer'
import { RootStore } from '../../redux/store'
import { settingsService } from '../../services/settingsService'
import { getWallChartData, getWallChartDataV2 } from '../../services/wallchartServices'
import { EventPopup } from '../../shared/UI/EventPopup'
import { PopupFields, popupFieldsDefault } from '../../shared/UI/EventPopup/types'
import { statusTranslation, typeTranslation } from '../../shared/UI/EventPopup/utils'
import LoadingIndicator from '../../shared/UI/LoadingIndicator'
import NoResult from '../../shared/UI/NoResult'
import PageHeader from '../../shared/layout/PageHeader/PageHeader'
import { isMobileDown } from '../../theme/deviceChecks'
import { getPalleteTypeByProp } from '../../theme/palette'
import { BaseResponse } from '../../types/base-response'
import { DepartmentThresholdsResponse } from '../../types/department-thresholds-response'
import {
  Day,
  WallChartItems,
  WallChartQueryProps,
  WallChartResponseBody,
} from '../../types/wallchart'
import {
  formatDateWithTimeZone,
  formatDateWithTimeZoneStr,
  groupDatesIntoRanges,
} from '../../utils/date-utils'
import UserErrorMessage from '../../utils/errorFilter'
import { PALETTE_TYPE_NOT_AVAILABLE, PALETTE_TYPE_PENDING_REQUESTS } from '../../utils/constants'
import LegendsFooter from '../LegendsFooter/index'
import { LegendProps } from '../LegendsFooter/types'
import { WallChartBackdrop } from './WallChartBackdrop'
import { WallChartFilters } from './WallChartFilters'
import { WallChartHeader } from './WallChartHeader'
import { WallChartRow } from './WallChartRow/WallChartRow'
import { WallChartTimespanSelector } from './WallChartTimespanSelector'
import { WallChartSelectedTimeSpan } from './WallChartTimespanSelector/types'
import {
  RowName,
  WallChartCard,
  WallChartContainer,
  WallChartRowItem,
  WallChartRowsBoxContainer,
} from './components'
import {
  ThresholdApiResponse,
  ThresholdDates,
  WallChartItemProps,
  WallChartItemsGrouped,
  WallChartProps,
} from './types'
import { buildThresholdsDayList } from './utils'
import { WallChartToggles, WallChartToggleType } from './WallChartToggles/WallChartToggles'
import { setRequestTypeFilters, setWallChartSelected, setWallChartSelectable, setWallChartAvailableEmployees } from '../../redux/reducers/wallChartReducer'

export const getRelatedEvent = (
  hoverPointDate: Date | undefined,
  userDisplayName: string,
  filterEvents: WallChartItems[]
): WallChartItemsGrouped | undefined => {
  if (!hoverPointDate) {
    return
  }
  let coincidingEvents = filterEvents.filter(item => item.userDisplayName === userDisplayName)[0]
  // Create non-referenced clone
  coincidingEvents = JSON.parse(JSON.stringify(coincidingEvents))
  // Check for coinciding events
  coincidingEvents.items = coincidingEvents.items.filter(eventItem =>
    // Is hovered event inside of another
    isWithinInterval(new Date(format(new Date(hoverPointDate), 'yyyy-MM-dd')), {
      start: new Date(format(new Date(eventItem.dateFrom), 'yyyy-MM-dd')),
      end: new Date(format(new Date(eventItem.dateTo), 'yyyy-MM-dd')),
    })
  )
  return coincidingEvents
}

const buildDaysArray = (users: WallChartItems[]) => {
  const result = users.map(user => {
    const absenceWithDays = user.items.filter(item => item.days.length)
    const absenceWithNoDays = user.items.filter(item => !item.days.length)
    const absenceWithDaysAdded = absenceWithNoDays.map(absence => ({
      ...absence,
      days: eachDayOfInterval({
        start: new Date(absence.dateFrom),
        end: new Date(absence.dateTo || absence.dateFrom),
      }).map(day => ({
        date: day,
        hours: 0,
      })),
    }))
    return {
      userDisplayName: user.userDisplayName,
      employeeId: user.employeeId,
      items: absenceWithDays.concat(absenceWithDaysAdded),
    }
  })
  return result
}

const batchDateRanges = (item: WallChartItems): WallChartItemsGrouped => {
  const groupedItems: WallChartItemProps[] = []
  item.items.forEach(it => {
    const dateRanges = groupDatesIntoRanges(it.days.map(d => d.date))
    dateRanges.forEach(range => {
      if (!range) {
        return
      }
      const [from, to] = range
      const days: Day[] = []
      it.days.forEach(itd => {
        const itemTimestamp = formatDateWithTimeZone(itd.date).getTime()
        if (itemTimestamp >= from.getTime() && itemTimestamp <= to.getTime()) {
          days.push(itd)
        }
      })
      const hours = days.reduce((a, b) => a + b.hours, 0)
      groupedItems.push({
        id: it.id,
        dateFrom: new Date(range?.[0] || ''),
        dateTo: new Date(range?.[1] || ''),
        recordTypeStr: it.recordTypeStr,
        status: it.status,
        hours,
        days,
        isCancellation: it.isCancellation,
        isQueried: it.isQueried,
      })
    })
  })
  return {
    userDisplayName: item.userDisplayName,
    employeeId: item.employeeId,
    items: groupedItems.length ? [...groupedItems] : [],
  }
}

const filterData = (result: WallChartResponseBody): WallChartResponseBody => {
  let filtered = result.items.map(item => ({
    userDisplayName: item.userDisplayName,
    employeeId: item.employeeId,
    items: item.items.filter(
      it =>
        it.dateFrom &&
        it.status?.toLowerCase() !== 'cancelled' &&
        it.status?.toLowerCase() !== 'declined' &&
        it.recordTypeStr?.toLowerCase().trim() !== 'workfromhome' &&
        it.recordTypeStr?.toLowerCase() !== 'late'
    ),
  }))
  if (filtered.length) {
    filtered = buildDaysArray(filtered)
    filtered = filtered.map(item => batchDateRanges(item))

    return { items: filtered, thresholds: result.thresholds } as WallChartResponseBody
  }
  return { items: filtered, thresholds: result.thresholds } as WallChartResponseBody
}

function WallChart({
  title,
  isWallChartView = true,
  filterPropertiesOverride,
  timeSpanPropertiesOverride,
}: WallChartProps) {
  const dispatch = useDispatch()
  const [wallChartData, setWallChartData] = useState<WallChartResponseBody>()
  const [legends, setLegends] = useState<LegendProps[]>([])
  const [popupProps, setPopupProps] = useState<PopupFields[]>([popupFieldsDefault])
  const [filterEvents, setFilterEvents] = useState<WallChartItems[]>([])
  const [ganttAreaLoading, setGanttAreaLoading] = useState(false)
  const [thresholds, setThresholds] = useState<DepartmentThresholdsResponse>()
  const [thresholdDates, setThresholdDates] = useState<ThresholdDates[]>()
  const [thresholdApiResponse, setThresholdApiResponse] = useState<ThresholdApiResponse[]>()
  const [localTimeSpan, setLocalTimespan] = useState<WallChartSelectedTimeSpan>()
  const [loadCount, setLoadCount] = useState<number>(40)
  const [backdropPos, setBackdropPos] = useState(0)
  const [showFilters, setShowFilters] = useState<boolean>(false)

  const { selectedParams, selectedTimeSpan, requestTypeFilters } = useSelector(
    (state: RootStore) => state.wallChart
  )

  useEffect(() => {
    setBackdropPos(0)
    if (isWallChartView) {
      setLocalTimespan(selectedTimeSpan)
    } else {
      setLocalTimespan(timeSpanPropertiesOverride)
    }
  }, [selectedTimeSpan, timeSpanPropertiesOverride, isWallChartView])

  const mobile = useMediaQuery(isMobileDown())

  const userPermissions = useSelector<RootStore, string[]>(
    (state: RootStore) => state.userState.permissions
  )

  const { employeeDetails, allEmployees } = useSelector((state: RootStore) => state.appSettings)

  const isManager = useMemo(
    () => userPermissions.includes('ViewManagerDirectReport'),
    [userPermissions]
  )

  const hierarchyEmployeeIds = useMemo(
    () => new Set(allEmployees.filter(e => e.inHierarchy).map(e => e.employeeId)),
    [allEmployees]
  )

  useEffect(() => {
    if (!selectedParams.selectedDepartment || selectedParams.selectedDepartment.value === 0) {
      setLegends([])
      return
    }
    settingsService
      .getThresholds(selectedParams.selectedDepartment.value)
      .then(result => {
        setThresholds(result)
      })
      .catch(err => {
        const response: BaseResponse = err.response.data
        if (response.errors && response.errors.length > 0) {
          response.errors.forEach(error => {
            dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
          })
        }
      })
  }, [selectedParams.selectedDepartment, dispatch])

  const { userSettings } = useSelector((state: RootStore) => state.appSettings)
  const isEnhancementsSettingOn = useMemo(
    () => userSettings?.hasEnhancements ?? false,
    [userSettings]
  )

  const wallChartRequestQuery = useMemo(() => {
    if (!isWallChartView && filterPropertiesOverride) {
      return filterPropertiesOverride
    }

    if (
      !selectedParams.selectedDepartment ||
      !selectedParams.selectedYear
    ) {
      return null
    }

    const currDate = new Date()
      const dateFrom = formatDateWithTimeZoneStr(
        selectedTimeSpan.startDate || startOfMonth(currDate)
      )
      const dateTostr = format(selectedTimeSpan.endDate, 'yyyy-MM-dd')

      const dateTo =
        dateTostr || formatDateWithTimeZoneStr(endOfMonth(formatDateWithTimeZone(currDate)))
      return {
        dateFrom,
        dateTo,
        departmentId: selectedParams.selectedDepartment.value,
        directReportees: userPermissions.includes('ViewManagerDirectReport')
          ? selectedParams.directReport
          : false,
        departmentTeamIds: selectedParams.selectedTeams.map(team => team.teamId),
        employees: []
      }
  }, [
    isWallChartView,
    selectedParams.selectedDepartment,
    selectedParams.selectedYear,
    selectedParams.selectedTeams,
    selectedParams.directReport,
    filterPropertiesOverride,
    selectedTimeSpan,
    userPermissions
  ])

  useEffect(() => {
    if (!wallChartRequestQuery) {
      return
    }

    setGanttAreaLoading(true)
    setLoadCount(40)
    const controller = new AbortController()
    const wallChartServiceMethod =
      isEnhancementsSettingOn && isManager ? getWallChartDataV2 : getWallChartData
    wallChartServiceMethod(wallChartRequestQuery, controller)
      .then(result => {
        const filtered = filterData(result)

        const mappedEmployees = filtered.items.map(item => ({
          employeeId: item.employeeId,
          employeeName: item.userDisplayName,          
          departmentId: 0,
          teamId: 0
        }))

        setThresholdApiResponse(filtered.thresholds)
        setWallChartData(filtered)

        dispatch(setWallChartAvailableEmployees(mappedEmployees))

        setGanttAreaLoading(false)
        setBackdropPos(0)
      })
      .catch(err => {
        if (axios.isCancel(err)) {
          return
        }

        const response: BaseResponse = err.response.data
        if (response.errors && response.errors.length > 0) {
          response.errors.forEach(error => {
            dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
          })
        }
        setGanttAreaLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [dispatch, selectedTimeSpan, isEnhancementsSettingOn, wallChartRequestQuery, isManager])

  const visibleWallChartData = useMemo(() => {
    if (!selectedParams.selectedEmployees?.length || !wallChartData || !isWallChartView) {
      return wallChartData
    }
    
    return {
      items: wallChartData.items.filter(item =>
        item.employeeId === employeeDetails.employeeId ||
        selectedParams.selectedEmployees.some(emp => emp.employeeId === item.employeeId)),
      thresholds: wallChartData.thresholds
    }

  }, [selectedParams.selectedEmployees, wallChartData, employeeDetails, isWallChartView])

  const isInRange = useCallback(
    (itemStartDate: Date, itemEndDate: Date) => {
      if (
        !localTimeSpan ||
        itemStartDate.getFullYear() < 2000 ||
        itemEndDate.getFullYear() < 2000
      ) {
        return false
      }
      return (
        // Is current event's start date within the timespan?
        isWithinInterval(itemStartDate, {
          start: localTimeSpan.startDate,
          end: localTimeSpan.endDate,
        }) ||
        // Is current event's end date within the timespan?
        isWithinInterval(itemEndDate, {
          start: localTimeSpan.startDate,
          end: localTimeSpan.endDate,
        }) ||
        // Do the current event's start and end dates straddle the entire timespan?
        isWithinInterval(localTimeSpan.startDate, {
          start: itemStartDate,
          end: itemEndDate,
        })
      )
    },
    [localTimeSpan]
  )

  useEffect(() => {
    const filtered: WallChartItems[] = visibleWallChartData?.items || []
    const sortedWithEvents = filtered
      .filter(item => item.items.length)
      .sort((a, b) => (a.userDisplayName > b.userDisplayName ? 1 : -1))
    const sortedWithoutEvents = filtered
      .filter(item => !item.items.length)
      .sort((a, b) => (a.userDisplayName > b.userDisplayName ? 1 : -1))
    const derivedEvents = sortedWithEvents.concat(sortedWithoutEvents)
    setFilterEvents(derivedEvents)
    // At present, we only use colouring threshold indicators when a single team is selected
    if (localTimeSpan && selectedParams?.selectedTeams?.length === 1 && thresholds) {
      const deptThresholds = buildThresholdsDayList(
        thresholdApiResponse,
        localTimeSpan.startDate,
        localTimeSpan.endDate,
        selectedParams.selectedTeams[0].teamId,
        thresholds
      )
      setThresholdDates(deptThresholds)
    } else {
      setThresholdDates([])
    }
  }, [isInRange, visibleWallChartData?.items, localTimeSpan, selectedParams, thresholdApiResponse, thresholds])

  useEffect(() => {
    const types: LegendProps[] = []
    
    if (isManager) {
      const pendingRequestLabel = getPalleteTypeByProp(PALETTE_TYPE_PENDING_REQUESTS)
      types.push(pendingRequestLabel)
    } else {
      const notAvailableLabel = getPalleteTypeByProp(PALETTE_TYPE_NOT_AVAILABLE)
      types.push(notAvailableLabel)
    }

    filterEvents?.forEach(person => {
      person?.items?.forEach(absenceEvent => {
        const localType = absenceEvent.recordTypeStr.replace(' (M)', '').replace(' Day', '')
        let recordTypeStr = getPalleteTypeByProp(localType)
        if (!recordTypeStr) {
          recordTypeStr = getPalleteTypeByProp('unknown')
        }
        return !types.includes(recordTypeStr) && types.push(recordTypeStr)
      })
    })
    setLegends(types)
  }, [filterEvents, isManager])

  const truncateName = (label: string): string => {
    if (label.indexOf(' ') < 0) {
      return label
    }
    const nameSplit = label.split(' ')
    return `${nameSplit[0]} ${nameSplit[1].substring(0, 1)}`
  }

  const handleScroll = (e: any) => {
    setPopupProps([popupFieldsDefault])
    setBackdropPos(e.target.scrollTop)
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 10
    if (bottom) {
      return setLoadCount(current => current + 10)
    }
    const top = e.target.scrollTop === 0
    if (top) {
      setLoadCount(40)
    }
  }

  const calculateStatusLabel = (prop: WallChartItemProps) => {
    if (!prop.status) return

    if (prop.isQueried) {
      return 'Queried'
    }
    return statusTranslation(prop.status)
  }

  const onFilterClick = () => setShowFilters(oldValue => !oldValue)
  const onToggle = (toggleType: WallChartToggleType) => {
    switch (toggleType) {
      case 'direct-reports':
        return dispatch(
          setWallChartSelected({ ...selectedParams, directReport: !selectedParams.directReport })
        )
      case 'wfh':
        return dispatch(
          setRequestTypeFilters({
            ...requestTypeFilters,
            showWorkFromHome: !requestTypeFilters.showWorkFromHome,
          })
        )
      case 'enhancements':
        return dispatch(
          setRequestTypeFilters({
            ...requestTypeFilters,
            showEnhancements: !requestTypeFilters.showEnhancements,
          })
        )
      default:
        break
    }
  }

  return (
    <>
      {isWallChartView && <PageHeader title="Wall Chart" />}
      <WallChartCard title={title} isWallChartView={isWallChartView}>
        <Stack direction="column" spacing={2} minHeight="0">
          <>
            {isWallChartView && selectedParams && (
              <WallChartTimespanSelector onFilterClick={onFilterClick} />
            )}
          </>
          {isWallChartView && (
            <Box sx={{ '&&': { marginTop: '0px' } }}>
              <Collapse
                in={showFilters}
                sx={{
                  width: '100%',
                  minHeight: 'auto',
                }}
              >
                <WallChartFilters directReportEnabled={!ganttAreaLoading} marginTop="16px" />
              </Collapse>
            </Box>
          )}
          {isWallChartView && isManager && (
            <WallChartToggles
              togglesSelected={{
                wfh: requestTypeFilters.showWorkFromHome,
                enhancements: requestTypeFilters.showEnhancements,
                directReport: selectedParams.directReport,
              }}
              disabled={ganttAreaLoading}
              onToggle={onToggle}
            />
          )}
          <>
            <LoadingIndicator
              show={ganttAreaLoading}
              margin="50px 0"
              data-testid="loading-indicator"
            />
            {!ganttAreaLoading && (
              <>
                <Box
                  margin={isWallChartView ? '16px 0 0 0' : ''}
                  onMouseLeave={e => setPopupProps([popupFieldsDefault])}
                  display="flex"
                  flexDirection="column"
                  minHeight="0"
                >
                  <>
                    {popupProps[0]?.open && (
                      <EventPopup
                        fields={popupProps}
                        baseUrl="wallchart"
                        onClose={() => setPopupProps([popupFieldsDefault])}
                        hideDeleteButton
                      />
                    )}
                    {selectedParams && localTimeSpan ? (
                      <WallChartHeader
                        timespanStartDate={localTimeSpan?.startDate}
                        timespanSegments={localTimeSpan?.segments}
                        dayIndexOffset={
                          localTimeSpan?.startDate.getDay() === 1
                            ? 0
                            : Number(localTimeSpan?.startDate.getDay()) + 1
                        }
                      />
                    ) : (
                      <></>
                    )}
                    <WallChartRowsBoxContainer
                      data-testid="wall-chart-rows-box-container"
                      onScroll={handleScroll}
                    >
                      <WallChartBackdrop
                        thresholdDates={thresholdDates}
                        scrollPosition={backdropPos}
                        selectedTimeSpan={localTimeSpan || selectedTimeSpan}
                      />
                      {selectedParams &&
                        localTimeSpan &&
                        visibleWallChartData &&
                        filterEvents.slice(0, loadCount).map((employee, idx) => {
                          const employeeRecord = allEmployees.find(e => e.employeeId === employee.employeeId)
                          let employeeLocationId = employeeRecord?.locationId

                          if (!employeeLocationId && employee.employeeId === employeeDetails.employeeId) {
                            employeeLocationId = employeeDetails.location.id
                          }

                          return (
                          <Box display="flex" key={employee.employeeId}>
                            <WallChartRowItem
                              sx={{
                                zIndex: 1,
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <Tooltip title={employee.userDisplayName} placement="right">
                                <RowName>{truncateName(employee.userDisplayName)}</RowName>
                              </Tooltip>
                            </WallChartRowItem>
                            <WallChartRowItem flexGrow={1}>
                              <WallChartRow
                                timespanStartDate={localTimeSpan.startDate}
                                timespanEndDate={localTimeSpan.endDate}
                                timespanSegments={localTimeSpan.segments}
                                eventItem={employee}
                                employeeLocationId={employeeLocationId}
                                thresholdDates={thresholdDates}
                                requestTypeFilters={requestTypeFilters}
                                onItemInfo={(target, _item, userDisplayName, hoverDate) => {
                                  const hasNoVisibleRequest = !_item || _item.recordTypeStr === '' || !isManager
                                  if (hasNoVisibleRequest) {
                                    return
                                  }

                                  const employeeOutOfHierarchy =
                                    !hierarchyEmployeeIds.has(employee.employeeId) &&
                                    employee.employeeId !== employeeDetails.employeeId
                                  if (employeeOutOfHierarchy) {
                                    return
                                  }

                                  const popUpProps = getRelatedEvent(hoverDate, userDisplayName, filterEvents)
                                  if (!popUpProps) {
                                    return
                                  }

                                  const { id, hours, recordTypeStr, isCancellation } = _item
                                  setPopupProps([
                                    {
                                      open: true,
                                      id,
                                      hours: hours || 0,
                                      eventType: typeTranslation(recordTypeStr),
                                      requestType: recordTypeStr,
                                      anchorEl: target,
                                      disableEdit:
                                        employeeDetails.employeeId === employee.employeeId,
                                      eventStatus: isCancellation
                                        ? 'Cancellation Requested'
                                        : calculateStatusLabel(_item),
                                    },
                                  ])
                                }}
                                onRowClick={() => {
                                  setPopupProps([popupFieldsDefault])
                                }}
                              />
                            </WallChartRowItem>
                          </Box>
                        )})}
                    </WallChartRowsBoxContainer>
                    {!visibleWallChartData?.items.length && <NoResult message="No Results" showImage />}
                  </>
                </Box>
                <LegendsFooter
                  legendProps={legends}
                  onSelected={item => undefined}
                  sx={{ marginTop: '16px' }}
                />
              </>
            )}
          </>
        </Stack>
      </WallChartCard>
    </>
  )
}

export default WallChart
